import { inject, Injectable, signal } from '@angular/core';
import { DocGenPdfStatusEnum } from '@domain';
import { TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import { Subscription } from 'rxjs';
import { DocumentController } from 'src/app/domain/controllers/document/document.controller';
import { DocumentPdfController } from 'src/app/domain/controllers/document/document-pdf.controller';
import {
    downloadFileFromUrl,
    openFileInNewTab,
    pdfFileNameFromTitle,
} from '../utils/file-download.util';
import { PusherService } from '../utils/pusher/pusher.service';

/**
 * Orquestra a geração de PDF de um documento de forma **não-bloqueante**, com a barra
 * de progresso dentro de um toast (cancelável). Usado pelo editor e pelo preview.
 *
 * O serviço é dono de todo o ciclo: dispara o request, assina os eventos do canal do
 * documento (`DOCUMENT-<id>`) — progresso/conclusão/erro — anima a barra suavemente,
 * atualiza o toast e baixa o arquivo no fim. Como assina o Pusher por conta própria
 * (não via lifecycle de página), a geração **sobrevive à navegação** entre editor e
 * outras telas. Apenas uma geração por vez (singleton em root).
 */
@Injectable({ providedIn: 'root' })
export class DocumentPdfGenerationService {
    /** Tempo médio total de geração medido em produção — calibra a velocidade da barra. */
    private static readonly EXPECTED_TOTAL_MS = 180_000;
    private static readonly PROGRESS_TICK_MS = 200;
    /** Folga abaixo do teto onde o avanço suave para, reservando o salto ao próximo milestone. */
    private static readonly PROGRESS_CEILING_GAP = 0.5;
    /** Incremento por tick para preencher 100% em EXPECTED_TOTAL_MS (velocidade constante). */
    private static readonly PROGRESS_STEP_PER_TICK =
        (100 * DocumentPdfGenerationService.PROGRESS_TICK_MS) /
        DocumentPdfGenerationService.EXPECTED_TOTAL_MS;
    /** Sem nenhum evento neste tempo, ressincroniza pelo servidor (cobre o caso de
     *  pedido em documento já COMPLETED, onde a API não reemite eventos). */
    private static readonly WATCHDOG_MS = 8_000;

    private _pdfController = inject(DocumentPdfController);
    private _documentController = inject(DocumentController);
    private _pusherService = inject(PusherService);
    private _toast = inject(RaToastService);
    private _translate = inject(TranslateService);

    /** `true` enquanto há uma geração em andamento — para travar botões de "Gerar PDF". */
    public readonly generating = signal(false);
    /** Última geração concluída — telas podem reagir (ex.: preview reexibir "Baixar PDF"). */
    public readonly completed = signal<{ documentId: string; pdfURL: string } | null>(null);

    private _documentId: string | null = null;
    /** Título do documento — vira o nome do arquivo baixado. */
    private _documentTitle: string | undefined;
    private _toastId: string | null = null;
    private _progress = 0;
    private _progressTimer: ReturnType<typeof setInterval> | null = null;
    private _watchdog: ReturnType<typeof setTimeout> | null = null;
    private _channelSub: Subscription | null = null;

    /**
     * Inicia uma geração: dispara o request e, em sucesso, abre o toast de progresso
     * e passa a escutar o canal do documento.
     *
     * @param documentTitle título do documento — usado como nome do arquivo baixado.
     */
    public start(documentId: string, documentTitle?: string): void {
        if (this.generating() || !documentId) return;

        this._pdfController.request(documentId).subscribe({
            next: () => this._begin(documentId, documentTitle),
            error: () => this._reset(),
        });
    }

    /**
     * Adota uma geração já em andamento (ex.: documento aberto com status
     * PENDING/CREATING) — abre o toast e escuta o canal, sem disparar novo request.
     */
    public adopt(documentId: string, documentTitle?: string): void {
        if (this.generating() || !documentId) return;
        this._begin(documentId, documentTitle);
    }

    private _begin(documentId: string, documentTitle?: string): void {
        this._documentId = documentId;
        this._documentTitle = documentTitle;
        this._progress = 0;
        this.generating.set(true);

        // Pill (ilha escura do rodapé): mesma faixa dos uploads de mídia — o card
        // ficava no canto esquerdo, por cima do conteúdo do editor.
        this._toastId = this._toast.show({
            type: 'info',
            variant: 'pill',
            icon: 'document-download',
            message: this._t('domain.document.pdf.generatingTitle'),
            persistent: true,
            dismissible: false,
            progress: 0,
            action: {
                label: this._t('common.cancel'),
                handler: () => this.cancel(),
            },
        });

        this._listenChannel(documentId);
        this._armWatchdog();
    }

    private _listenChannel(documentId: string): void {
        const channelName = `DOCUMENT-${documentId}`;
        const sub = new Subscription();

        sub.add(
            this._pusherService
                .subscribe(channelName, 'doc-pdf-progress')
                .subscribe((resp: { percent: number; ceiling: number; stage: string }) =>
                    this._onProgress(resp.percent, resp.ceiling)
                )
        );

        sub.add(
            this._pusherService
                .subscribe(channelName, 'doc-pdf-generated')
                .subscribe((resp: { pdfURL: string }) => this._onGenerated(resp?.pdfURL))
        );

        sub.add(
            this._pusherService
                .subscribe(channelName, 'doc-pdf-error')
                .subscribe(() => this._onError())
        );

        // Reconexão: eventos publicados durante a queda foram perdidos. Ressincroniza
        // pelo servidor para não travar o toast caso a conclusão tenha se perdido.
        sub.add(this._pusherService.onReconnected().subscribe(() => this._resyncOnReconnect()));

        this._channelSub = sub;
    }

    /**
     * Milestone de progresso do engine: salta a barra para `percent` (início da etapa) e
     * anima suavemente em direção a `ceiling` (fim da etapa), para a barra continuar
     * progredindo de forma crível durante etapas longas sem travar nem ultrapassar.
     */
    private _onProgress(percent: number, ceiling: number): void {
        if (!this._toastId) return;
        // Chegou evento real: a geração está de fato em andamento, dispensa o watchdog.
        this._clearWatchdog();
        this._stopAnimation();

        this._progress = Math.max(this._progress, percent);
        this._updateToast();

        const stopAt = ceiling - DocumentPdfGenerationService.PROGRESS_CEILING_GAP;
        this._progressTimer = setInterval(() => {
            const next = this._progress + DocumentPdfGenerationService.PROGRESS_STEP_PER_TICK;
            if (next >= stopAt) {
                this._progress = stopAt;
                this._updateToast();
                this._stopAnimation();
                return;
            }
            this._progress = next;
            this._updateToast();
        }, DocumentPdfGenerationService.PROGRESS_TICK_MS);
    }

    private _onGenerated(pdfURL?: string): void {
        this._stopAnimation();
        this._progress = 100;
        this._updateToast();

        if (this._documentId && pdfURL) {
            this.completed.set({ documentId: this._documentId, pdfURL });
        }

        // Guarda antes do reset, que limpa o estado da geração.
        const documentTitle = this._documentTitle;

        this._reset();

        if (!pdfURL) {
            this._toast.success({
                title: this._t('document.preview.pdfGeneratedTitle'),
                message: this._t('document.preview.pdfGeneratedMessage'),
            });
            return;
        }

        this._download(pdfURL, documentTitle);
    }

    private _onError(): void {
        this._toast.error({
            title: this._t('document.preview.pdfGenerationErrorTitle'),
            message: this._t('document.preview.pdfGenerationErrorMessage'),
        });
        this._reset();
    }

    /** Cancela a geração em andamento (ação do toast). */
    public cancel(): void {
        const documentId = this._documentId;
        this._reset();
        if (documentId) this._pdfController.cancel(documentId).subscribe();
    }

    /**
     * Ao reconectar, rebusca o status no servidor: se já concluiu/erro durante a queda,
     * resolve o toast; caso contrário, segue aguardando os próximos eventos.
     */
    private _resyncOnReconnect(): void {
        const documentId = this._documentId;
        if (!documentId) return;

        this._documentController.getById(documentId).subscribe({
            next: document => {
                if (document.pdfStatus === DocGenPdfStatusEnum.COMPLETED && document.pdfURL) {
                    this._onGenerated(document.pdfURL);
                } else if (document.pdfStatus === DocGenPdfStatusEnum.ERROR) {
                    this._onError();
                }
            },
        });
    }

    /**
     * Baixa o PDF pronto. Aqui estamos num callback do Pusher — sem gesto do usuário —,
     * então abrir aba nova seria barrado pelo popup blocker e nada aconteceria. O download
     * é feito a partir de um `blob:` URL (ver `file-download.util`).
     *
     * Se o arquivo não puder ser buscado (CORS/rede), o toast de sucesso vira um convite a
     * clicar: o clique é um gesto do usuário e aí a abertura do PDF é permitida.
     */
    private _download(url: string, documentTitle?: string): void {
        void downloadFileFromUrl(url, pdfFileNameFromTitle(documentTitle, url)).then(downloaded => {
            if (downloaded) {
                this._toast.success({
                    title: this._t('document.preview.pdfGeneratedTitle'),
                    message: this._t('document.preview.pdfGeneratedMessage'),
                });
                return;
            }

            this._toast.success({
                title: this._t('document.preview.pdfGeneratedTitle'),
                message: this._t('document.preview.pdfReadyManualMessage'),
                persistent: true,
                action: {
                    label: this._t('document.preview.pdfDownloadAction'),
                    handler: () => openFileInNewTab(url),
                },
            });
        });
    }

    private _updateToast(): void {
        if (this._toastId) {
            this._toast.update(this._toastId, { progress: Math.round(this._progress) });
        }
    }

    private _stopAnimation(): void {
        if (this._progressTimer !== null) {
            clearInterval(this._progressTimer);
            this._progressTimer = null;
        }
    }

    private _armWatchdog(): void {
        this._clearWatchdog();
        this._watchdog = setTimeout(
            () => this._resyncOnReconnect(),
            DocumentPdfGenerationService.WATCHDOG_MS
        );
    }

    private _clearWatchdog(): void {
        if (this._watchdog !== null) {
            clearTimeout(this._watchdog);
            this._watchdog = null;
        }
    }

    private _reset(): void {
        this._stopAnimation();
        this._clearWatchdog();
        this._channelSub?.unsubscribe();
        this._channelSub = null;
        if (this._toastId) {
            this._toast.dismiss(this._toastId);
            this._toastId = null;
        }
        this.generating.set(false);
        this._documentId = null;
        this._documentTitle = undefined;
        this._progress = 0;
    }

    private _t(key: string): string {
        return this._translate.instant(key);
    }
}
